import { useEffect, useState } from "react";
import { Download, ClipboardList } from "lucide-react";
import { PageHeader, StatCard } from "../components/Primitives";
import { api, errorText, inr, formatDate } from "../lib/api";

export default function Reports() {
  const [overview, setOverview] = useState({});
  const [payments, setPayments] = useState([]);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [error, setError] = useState("");
  const [range, setRange] = useState({ date_from: "", date_to: "" });

  const load = async () => {
    try {
      const [overviewRes, paymentsRes] = await Promise.all([
        api.get("/reports/overview"),
        api.get("/reports/payments", { params: range }),
      ]);
      setOverview(overviewRes.data.data);
      setPayments(paymentsRes.data.data);
      setPaymentTotal(paymentsRes.data.total);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const exportLink = (path) => `${process.env.REACT_APP_BACKEND_URL}/api${path}`;

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="ANALYSE"
        title="Reports"
        description="Operational health in one dashboard — exportable at any time."
        action={
          <div style={{ display: "flex", gap: 10 }}>
            <a className="button button-outline" href={exportLink("/reports/export/students")} target="_blank" rel="noreferrer" data-testid="export-students-btn"><Download size={16} /> Students</a>
            <a className="button button-outline" href={exportLink("/reports/export/payments")} target="_blank" rel="noreferrer" data-testid="export-payments-btn"><Download size={16} /> Payments</a>
            <a className="button button-outline" href={exportLink("/reports/export/bills")} target="_blank" rel="noreferrer" data-testid="export-bills-btn"><Download size={16} /> Bills</a>
          </div>
        }
      />
      {error ? <div className="error-message">{error}</div> : null}
      <div className="stat-grid">
        <StatCard label="Active students" value={overview.active_students ?? 0} detail="Current roster" icon={<ClipboardList size={18} />} />
        <StatCard label="Today's collection" value={inr(overview.today_collection)} detail="Payments today" tone="green" icon={<ClipboardList size={18} />} />
        <StatCard label="Month collection" value={inr(overview.month_collection)} detail="Current month" tone="orange" icon={<ClipboardList size={18} />} />
        <StatCard label="Overdue bills" value={overview.overdue_bills ?? 0} detail={`${overview.pending_bills ?? 0} pending`} tone="dark" icon={<ClipboardList size={18} />} />
      </div>

      <section className="content-card table-card">
        <div className="table-toolbar">
          <div>
            <span className="card-kicker">PAYMENTS</span>
            <h2>{payments.length} payments · {inr(paymentTotal)}</h2>
          </div>
          <div className="toolbar-actions">
            <input type="date" value={range.date_from} onChange={(event) => setRange({ ...range, date_from: event.target.value })} data-testid="reports-date-from" />
            <input type="date" value={range.date_to} onChange={(event) => setRange({ ...range, date_to: event.target.value })} data-testid="reports-date-to" />
            <button className="button button-outline" onClick={load} data-testid="apply-filters-button">Apply</button>
          </div>
        </div>
        <div className="table-wrap">
          <table data-testid="payments-report-table">
            <thead>
              <tr><th>Receipt</th><th>Student</th><th>Amount</th><th>Mode</th><th>Date</th></tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{payment.receipt_number}</td>
                  <td>{payment.student_name}</td>
                  <td>{inr(payment.amount)}</td>
                  <td>{payment.payment_mode}</td>
                  <td>{formatDate(payment.payment_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
